import { useEffect, useState } from "react";
import { Radio, CalendarPlus } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { markLiveJoin } from "@/lib/aiot";

const SOON_MS = 10 * 60000;

// Admins enter live_at as India wall time (datetime-local, no zone).
export const istDate = (at) => new Date(/[zZ]|[+-]\d\d:\d\d$/.test(at) ? at : `${at.length === 16 ? `${at}:00` : at}+05:30`);
const stamp = (d) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
const gcalUrl = (c, start, end, link) => `https://calendar.google.com/calendar/render?${new URLSearchParams({
  action: "TEMPLATE", text: `RoboSpark live class ${c.n}: ${c.title} (Simran Ma'am)`, dates: `${stamp(start)}/${stamp(end)}`,
  details: `Live AIoT Diploma class with Simran Ma'am.\nJoin: ${link}`, location: link })}`;
const mmss = (ms) => { const s = Math.ceil(ms / 1000); return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`; };

const useNow = () => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  return now;
};

// soon (10 min before) → countdown; live → "Live now"; both pulse the Join button.
const phaseOf = (now, start, end) => (!start ? "unscheduled" : now < start - SOON_MS ? "later" : now < start ? "soon" : now < end ? "live" : "ended");

export const LiveCard = ({ c, live, at }) => {
  const { L } = useLang();
  const now = useNow();
  const start = at ? istDate(at) : null;
  const end = start && new Date(start.getTime() + (c.minutes || 60) * 60000);
  const phase = phaseOf(now, start?.getTime(), end?.getTime());
  const hot = phase === "soon" || phase === "live";
  const when = start ? `${start.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Kolkata" })} IST` : L("Time shared by your trainer", "समय ट्रेनर बताएँगे");
  return (
    <div className={`rounded-3xl bg-[#0B1F3A] text-white p-6 flex flex-wrap items-center gap-4 ${hot ? "ring-2 ring-[#14B8A6]" : ""}`} data-testid="aiot-class-live" data-phase={phase}>
      <Radio className={`w-8 h-8 text-[#5EEAD4] ${hot ? "animate-pulse" : ""}`} />
      <div className="flex-1 min-w-[200px]">
        <div className="font-extrabold text-lg">{phase === "live" ? L("Live now", "अभी लाइव") : L("Live class", "लाइव क्लास")}</div>
        <div className="text-sm text-slate-300" data-testid="aiot-class-live-time">{when}</div>
        {phase === "soon" && <div className="mt-1 text-sm font-bold text-[#5EEAD4]" data-testid="aiot-class-countdown">{L("Starts in", "शुरू होने में")} <span className="font-mono text-lg tabular-nums">{mmss(start - now)}</span></div>}
        {phase === "ended" && <div className="mt-1 text-xs text-slate-400" data-testid="aiot-class-ended">{L("This live class has ended.", "यह लाइव क्लास खत्म हो चुकी है।")}</div>}
      </div>
      {(phase === "later" || phase === "soon") && <a href={gcalUrl(c, start, end, live)} target="_blank" rel="noreferrer" className="rounded-full border border-white/30 px-5 h-11 inline-flex items-center gap-2 font-bold hover:bg-white/10 transition-colors" data-testid="aiot-class-gcal"><CalendarPlus className="w-4 h-4" />{L("Add to Google Calendar", "Google कैलेंडर में जोड़ें")}</a>}
      <a href={live} target="_blank" rel="noreferrer" data-testid="aiot-class-join" onClick={() => markLiveJoin(c.id)}
        className={`relative rounded-full bg-[#14B8A6] text-[#0B1F3A] px-5 h-11 inline-flex items-center font-bold hover:bg-white transition-colors ${hot ? "shadow-[0_0_0_6px_rgba(20,184,166,0.25)]" : ""}`}>
        {hot && <span className="absolute inset-0 rounded-full bg-[#14B8A6] animate-ping opacity-40" aria-hidden="true" />}
        <span className="relative">{hot ? L("Join now", "अभी जॉइन करें") : L("Join live class", "लाइव क्लास जॉइन करें")}</span>
      </a>
    </div>
  );
};
